// SelectInput.jsx
import { useField, useFormikContext } from 'formik'
import React, { CSSProperties, useMemo } from 'react'
import ReactSelect, { GroupBase, OptionsOrGroups } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { get_request_api } from '../../service/misc'
import { useQuery } from '@tanstack/react-query'
import { getAddress, getAddresses } from '../address'
import { useTranslation } from 'react-i18next'
import { CFieldProps, GroupedOption } from '@velocity/shared'

type InputProps = CFieldProps & {
  label?: string
  disabled?: boolean
  isMulti?: boolean
  options?: OptionsOrGroups<any, GroupBase<any>>
  isLoading?: boolean
  validator?: any
  placeholder?: string
  onInputChange?: any
  loadOptions?: any
  defaultOptions?: any
  // searchFn?: boolean
  hideIndicator?: boolean
}
export const CSelect = ({
  validator,
  hideIndicator,
  className,
  required,
  ...props
}: InputProps) => {
  const { t } = useTranslation()
  const formik = useFormikContext<any>()
  const [createVal, setCreateVal] = React.useState<string>('')
  const [, meta, helpers] = useField(props.name)
  const [search, setSearch] = React.useState<string>()
  const { data, isFetching }: any = useQuery({
    queryKey: [
      props.optionsQuery?.queryKey[0] || '',
      (props.optionsQuery?.queryKey[1] || '') + (search || '')
    ],
    queryFn: props.selectProps?.requestFn || get_request_api,
    enabled: props.optionsQuery?.enabled || false
  })
  const options = useMemo(() => {
    let options = props.options || []
    if (!props.options && data?.rows)
      options = data.rows.map((item: any) => ({
        label:
          props.optionsQuery?.name ?
            item[props.optionsQuery?.name] || item.name
          : item.name,
        value: props.optionsQuery?.id ? item[props.optionsQuery?.id] : item.id
      }))
    if (props.selectProps?.extraOptions) {
      options = props.selectProps.extraOptions.concat(options)
    }
    if (props.selectProps?.translateOptions) {
      options = options.map((option: any) => ({
        label: option.label ? t(option.label) : option.label,
        value: option.value
      }))
    }
    return options
    // eslint-disable-next-line
  }, [data, props.options])
  const value = useMemo(() => {
    const v = createVal || meta.value
    const opns =
      props.grouped ? options.flatMap((o: any) => o.options) : options
    return (
      v?.toString()?.length ?
        props.isMulti ?
          (v as any)?.map(
            (val: any) =>
              opns?.find((o: any) => o.value === val) || {
                value: val,
                label: props.useValueAsLabel ? val : ''
              }
          )
        : {
            value: v,
            label:
              opns?.find((o: any) => o.value === v)?.label ||
              props.selectProps?.fallBackLabel ||
              (props.useValueAsLabel ? v : '')
          }
      : null
    )
    // eslint-disable-next-line
  }, [meta.value, props.isMulti, createVal, options])
  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
  const groupBadgeStyles: CSSProperties = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center'
  }

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      {!data.noCount && (
        <span style={groupBadgeStyles}>{data.options.length}</span>
      )}
    </div>
  )
  const onChange = (val: any) => {
    setCreateVal('')
    if (props.type === 'address') {
      getAddress(props.name, val, formik, props.addressProps?.fields)
    } else if (props.createFn && val.__isNew__) {
      props.createFn(val)
      setCreateVal(val.value)
    } else if (props.selectFn) {
      props.selectFn(val, props.name)
    } else {
      if (props.isMulti) {
        helpers.setValue(val.map((v: any) => v.value))
      } else {
        helpers.setValue(val.value)
      }
    }
  }
  const onInputChange = (inputValue: string) => {
    props.onInputChange?.(inputValue)
    if (props.optionsQuery?.search)
      setSearch(
        (props.optionsQuery?.queryKey[1] ? '&' : '') + 'search=' + inputValue
      )
  }
  const commonProps: any = useMemo(
    () => ({
      ...props,
      components:
        hideIndicator ?
          {
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
          }
        : {},
      styles: {
        control: (base: any) => ({
          ...base,
          borderColor:
            meta?.touched && meta?.error ? '#ef4444' : base.borderColor,
          '&:hover': {
            borderColor:
              meta?.touched && meta?.error ? '#ef4444' : base.borderColor
          }
        }),
        placeholder: (base: any) => ({
          ...base,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', // truncate long placeholder
          maxWidth: 'calc(100% - 20px)'
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
      },
      isDisabled: props.disabled,
      menuPortalTarget: document.body,
      className: 'basic-multi-select',
      classNamePrefix: 'react-select',
      onChange,
      isLoading: isFetching || props.isLoading,
      onInputChange,
      options,
      loadOptions: props.type === 'address' ? getAddresses : props.loadOptions,
      value,
      menuShouldScrollIntoView: true,
      cacheOptions: true
      // menuPlacement:"auto"
    }),
    // eslint-disable-next-line
    [value, options, isFetching, props.isLoading]
  )
  if (props.grouped) commonProps['formatGroupLabel'] = formatGroupLabel
  // console.log(commonProps)
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {(props.createFn || props.createAble) && props.loadOptions ?
        <AsyncCreatableSelect {...commonProps} defaultOptions />
      : props.createFn || props.createAble ?
        <CreatableSelect
          {...commonProps}
          isValidNewOption={(inputValue: any) =>
            validator ? validator(inputValue) : true
          }
        />
      : props.loadOptions || props.type === 'address' ?
        <AsyncSelect {...commonProps} defaultOptions />
      : <ReactSelect {...commonProps} />}
    </div>
  )
}
